import { V2ProductionCostGetGroupEnergyUnitListPostResponse } from '@maxtropy/device-customer-apis-v2';
import { DataNode } from 'antd/es/tree';
import electricityActive from './imgs/electricityActive.png';
import electricityPassive from './imgs/electricityPassive.png';
import waterActive from './imgs/waterActive.png';
import waterPassive from './imgs/waterPassive.png';
import gasActive from './imgs/gasActive.png';
import gasPassive from './imgs/gasPassive.png';
import hotSteamActive from './imgs/hotSteamActive.png';
import hotSteamPassive from './imgs/hotSteamPassive.png';
import compressedAirActive from './imgs/compressedAirActive.png';
import compressedAirPassive from './imgs/compressedAirPassive.png';
import IndustrialGasActive from './imgs/IndustrialGasActive.png';
import IndustrialGasPassive from './imgs/IndustrialGasPassive.png';
import haActive from './imgs/haActive.png';
import haPassive from './imgs/haPassive.png';
import IndustrialLiquidActive from './imgs/IndustrialLiquidActive.png';
import IndustrialLiquidPassive from './imgs/IndustrialLiquidPassive.png';
import { isNil } from 'lodash-es';
import { RangeValue } from './components/RightContent';

// 生成节点映射表（树形结构-->平铺）
export const generateList = (data: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']) => {
  let tempDataList: { key: React.Key; title: string }[] = [];
  const loopData = (data: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']) => {
    for (let i = 0; i < (data ?? []).length; i++) {
      const node = data?.[i];
      tempDataList.push({ key: node?.energyUnitId ?? '', title: node?.name ?? '' });
      if (node?.children) {
        loopData(node.children);
      }
    }
  };
  loopData(data);
  return tempDataList;
};

// 初始化展开的节点列表
export const initExpandedKeys = (data: DataNode[]) => {
  let keys: number[] = [];
  const loop = (nodeList: DataNode[]) => {
    for (const item of nodeList ?? []) {
      keys.push(item.key as number);
      if (item.children) {
        loop(item.children);
      }
    }
  };
  loop(data);
  return keys;
};

// 工具函数：递归收集所有子节点 key
export const getAllChildKeys = (node: any): string[] => {
  let keys: string[] = [];
  keys.push(node.key);
  if (node.children) {
    node.children.forEach((child: any) => {
      keys.push(child.key);
      keys = keys.concat(getAllChildKeys(child));
    });
  }
  return keys;
};

// 获取有权限的用能单元
export const initExpandedKeysWithPermission = (data: DataNode[]) => {
  let keys: number[] = [];
  const loop = (nodeList: DataNode[]) => {
    for (const item of nodeList ?? []) {
      if (!item.disabled) {
        keys.push(item.key as number);
      }
      if (item.children) {
        loop(item.children);
      }
    }
  };
  loop(data);
  return keys;
};

// 获取父节点key
export const getParentKey = (
  key: React.Key,
  tree: V2ProductionCostGetGroupEnergyUnitListPostResponse['list']
): React.Key => {
  let parentKey: React.Key;
  for (let i = 0; i < (tree ?? []).length; i++) {
    const node = tree?.[i];
    if (node?.children) {
      if (node.children.some(item => item.energyUnitId === key)) {
        parentKey = node?.energyUnitId ?? '';
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }
  return parentKey!;
};

// 递归查找某个节点下所有子节点 key
export const getAllDescendantKeys = (node: DataNode): string[] => {
  let keys = [node.key as string];
  if (node.children) {
    node.children.forEach(child => {
      keys = keys.concat(getAllDescendantKeys(child));
    });
  }
  return keys;
};

// 根据 key 在 treeData 中查找对应 node
export const findNodeByKey = (key: React.Key, nodes: DataNode[]): DataNode | null => {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(key, node.children);
      if (found) return found;
    }
  }
  return null;
};

// 全选或级联
export enum AllorCascader {
  // 全选
  ALL = 1,
  // 级联
  CASCADER = 2,
}

export const AllorCascaderDisplay = {
  [AllorCascader.ALL]: '全选',
  [AllorCascader.CASCADER]: '全选子节点',
};

// 时间颗粒度
export enum DateType {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export const DateTypeDisplay = {
  [DateType.DAY]: '按日',
  [DateType.WEEK]: '按周',
  [DateType.MONTH]: '按月',
  [DateType.YEAR]: '按年',
};

// 工质场景 id 枚举
export enum EnergyMediumSceneIds {
  WATER_SUPPLY_AND_DRAINAGE = 500, // 给排水/用气
  GAS_SUPPLY_AND_CONSUMPTION = 501, // 供气/用气
  HOT_STEAM = 502, // 热蒸汽
  COMPRESSED_AIR = 503, // 压缩空气
  INDUSTRIAL_GASES = 504, // 工业气体
  HEATING_VENTILATION_AND_AIR_CONDITIONING = 505, // 供暖通风与空气调节
  INDUSTRIAL_LIQUIDS = 506, // 工业液体
  ELEC = 507, // 电能
}

// 工质 icon 映射
export const EnergyMediumIconDisplay = {
  [EnergyMediumSceneIds.WATER_SUPPLY_AND_DRAINAGE]: {
    active: waterActive,
    passive: waterPassive,
  },
  [EnergyMediumSceneIds.GAS_SUPPLY_AND_CONSUMPTION]: {
    active: gasActive,
    passive: gasPassive,
  },
  [EnergyMediumSceneIds.HOT_STEAM]: {
    active: hotSteamActive,
    passive: hotSteamPassive,
  },
  [EnergyMediumSceneIds.COMPRESSED_AIR]: {
    active: compressedAirActive,
    passive: compressedAirPassive,
  },
  [EnergyMediumSceneIds.INDUSTRIAL_GASES]: {
    active: IndustrialGasActive,
    passive: IndustrialGasPassive,
  },
  [EnergyMediumSceneIds.HEATING_VENTILATION_AND_AIR_CONDITIONING]: {
    active: haActive,
    passive: haPassive,
  },
  [EnergyMediumSceneIds.INDUSTRIAL_LIQUIDS]: {
    active: IndustrialLiquidActive,
    passive: IndustrialLiquidPassive,
  },
  [EnergyMediumSceneIds.ELEC]: {
    active: electricityActive,
    passive: electricityPassive,
  },
};

/**
 * 获取默认时间
 */
export const getDefaultTime = (mode: DateType, date: RangeValue) => {
  let startTime = date?.[0]?.startOf('day').valueOf();
  let endTime = date?.[1]?.endOf('day').valueOf();

  switch (mode) {
    case DateType.WEEK:
      startTime = date?.[0]?.startOf('week').startOf('day').valueOf();
      endTime = date?.[1]?.endOf('week').endOf('day').valueOf();

      break;
    case DateType.MONTH:
      startTime = date?.[0]?.startOf('month').startOf('day').valueOf();
      endTime = date?.[1]?.endOf('month').endOf('day').valueOf();
      break;
    case DateType.YEAR:
      startTime = date?.[0]?.startOf('year').startOf('day').valueOf();
      endTime = date?.[1]?.endOf('year').endOf('day').valueOf();
      break;
  }
  return {
    startTime,
    endTime,
  };
};
